import { useState, useCallback, useEffect, useRef } from 'react';
import useToken from './useToken';

/**
 * Custom hook to manage balance state.
 * Provides fetchBalance, balance state, and auto-polling.
 * Replaces the DOM manipulation pattern in App.js and Deposit.js.
 */
function useBalance() {
  const { token, isLoggedIn, clearAuth } = useToken();
  const [balance, setBalance] = useState(() => {
    // Initialize from localStorage if available
    const cached = localStorage.getItem('balance');
    return cached ? Number(cached) : 0;
  });
  const mountedRef = useRef(true);

  const fetchBalance = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (mountedRef.current) {
          setBalance(data.balance);
          localStorage.setItem('balance', String(data.balance));
        }
      } else if (response.status === 401) {
        clearAuth();
      }
    } catch (error) {
      // silent fail - balance will show cached value
    }
  }, [token, clearAuth]);

  useEffect(() => {
    mountedRef.current = true;
    if (isLoggedIn) {
      fetchBalance();
      const interval = setInterval(fetchBalance, 10000);
      return () => {
        mountedRef.current = false;
        clearInterval(interval);
      };
    }
    return () => { mountedRef.current = false; };
  }, [isLoggedIn, fetchBalance]);

  return { balance, fetchBalance, setBalance };
}

export default useBalance;
